import { useCallback, useEffect, useRef } from 'react';
import { txToast } from '@/components/TxToaster';
import { useAdminPerms, useAuth, useCsrfToken } from '@/hooks/auth';
import { useAuthedFetcher } from '@/hooks/fetch';
import { startDeploymentNotifications, type DeploymentNotificationController } from '@/lib/deploymentNotifications';
import type { ResourceDeploymentStatusResp } from '@shared/deploymentApiTypes';

export function useDeploymentNotifications() {
    const { hasPerm } = useAdminPerms();
    const { authData } = useAuth();
    const accountName = authData ? authData.name : undefined;
    const csrfToken = useCsrfToken();
    const fetcher = useAuthedFetcher();
    const fetcherRef = useRef(fetcher);
    const controllerRef = useRef<DeploymentNotificationController>();
    fetcherRef.current = fetcher;
    const enabled = window.txConsts.isWebInterface && !!csrfToken && !!accountName && hasPerm('manage.admins');

    useEffect(() => {
        if (!enabled) return;
        let storage: Storage | undefined;
        try {
            storage = window.localStorage;
        } catch {
            // Notifications still work in browsers that disable local storage.
        }
        const controller = startDeploymentNotifications(
            (controller) => fetcherRef.current<ResourceDeploymentStatusResp>(
                '/deployment/status', { method: 'GET' }, controller,
            ),
            (deployment) => {
                txToast.success({
                    title: 'Commit recebido na VPS',
                    msg: `${deployment.commit.slice(0, 7)} sincronizado. Já podes reiniciar as resources para testar.`,
                }, { duration: 7_000, id: `deployment:${deployment.commit}` });
            },
            document,
            { accountName, storage },
        );
        controllerRef.current = controller;
        return () => {
            if (controllerRef.current === controller) controllerRef.current = undefined;
            controller();
        };
    }, [enabled, csrfToken, accountName]);

    const receive = useCallback((response: ResourceDeploymentStatusResp) => {
        controllerRef.current?.receive(response);
    }, []);
    const refresh = useCallback(() => {
        void controllerRef.current?.refresh();
    }, []);
    return { enabled, accountName, receive, refresh };
}
